import { useState, useEffect } from "react";
import { LoadingScreen, NoAccessScreen } from "../../components";
import { usePagePermission } from "../../Hook";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Download, SlidersHorizontal, RotateCcw, Send } from "lucide-react";
import { Spin, DatePicker, Input, Select, Drawer, Space, Button as AntdButton } from "antd";
import { FetchUsers_api } from "../../Api";
import { SERVER_API } from "../../Constant";
import axios from "axios";
import dayjs from "dayjs";
import { downloadExcel } from "../../lib/excelGenerator";

const { RangePicker } = DatePicker;

const toNumber = (value: any): number =>
  Number(String(value ?? "0").replace(/,/g, "")) || 0;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-LK", {
    style: "currency",
    currency: "LKR",
    minimumFractionDigits: 2,
  }).format(value);

export const DpmcRemittanceReport = () => {
  const { isLoading: permissionLoading, hasPermission } = usePagePermission(
    "dpmc_remittance_report" as any,
    ["admin", "cashier", "manager"],
  );

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<any[]>([]);
  const [totalAmount, setTotalAmount] = useState(0);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [addedUserId, setAddedUserId] = useState<number | undefined>(undefined);
  const [slipId, setSlipId] = useState("");
  const [inquiryNumber, setInquiryNumber] = useState("");
  const [hasApplied, setHasApplied] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<any>(null);
  const [draftAddedUserId, setDraftAddedUserId] = useState<number | undefined>(undefined);
  const [draftSlipId, setDraftSlipId] = useState("");
  const [draftInquiryNumber, setDraftInquiryNumber] = useState("");

  const [users, setUsers] = useState<any[]>([]);

  useEffect(() => {
    if (hasPermission) {
      FetchUsers_api(1, 1000, "").then((response: any) => {
        if (response && response.users) {
          setUsers(response.users);
        }
      });
    }
  }, [hasPermission]);

  const fetchReport = async (
    start: string,
    end: string,
    addedUser?: number,
    slip: string = "",
    inqNo: string = "",
  ) => {
    if (!start || !end) {
      alert("Please select both start and end dates");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(
        `${SERVER_API}/Reports/fetch_dpmc_remittance_report.php`,
        {
          params: {
            startDate: start,
            endDate: end,
            addedUser,
            slipId: slip,
            inquiryNumber: inqNo,
          },
        },
      );
      const res = response.data;
      if (res.success) {
        setReportData(res.data || []);
        setTotalAmount(Number(res.total) || 0);
      } else {
        alert(res.msg || "Failed to fetch report");
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const openDrawer = () => {
    setDraftRange(startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null);
    setDraftAddedUserId(addedUserId);
    setDraftSlipId(slipId);
    setDraftInquiryNumber(inquiryNumber);
    setDrawerOpen(true);
  };

  const applyFilters = () => {
    const s = draftRange?.[0] ? draftRange[0].format("YYYY-MM-DD") : "";
    const e = draftRange?.[1] ? draftRange[1].format("YYYY-MM-DD") : "";
    setStartDate(s);
    setEndDate(e);
    setAddedUserId(draftAddedUserId);
    setSlipId(draftSlipId.trim());
    setInquiryNumber(draftInquiryNumber.trim());
    setHasApplied(true);
    setDrawerOpen(false);
    fetchReport(s, e, draftAddedUserId, draftSlipId.trim(), draftInquiryNumber.trim());
  };

  const resetFilters = () => {
    setDraftRange(null);
    setDraftAddedUserId(undefined);
    setDraftSlipId("");
    setDraftInquiryNumber("");
    setStartDate("");
    setEndDate("");
    setAddedUserId(undefined);
    setSlipId("");
    setInquiryNumber("");
    setReportData([]);
    setTotalAmount(0);
    setHasApplied(false);
    setDrawerOpen(false);
  };

  const periodLabel = startDate && endDate ? `${startDate} to ${endDate}` : "";
  const activeUserName = users.find((u) => u.id === addedUserId)?.username || "";
  const filterSummary = [
    periodLabel,
    activeUserName,
    slipId ? `Slip: ${slipId}` : "",
    inquiryNumber ? `Inquiry: ${inquiryNumber}` : "",
  ].filter(Boolean).join(" | ");

  const handleDownloadExcel = () => {
    const headers = [
      "Date",
      "Transfer ID",
      "Inquiry IDs",
      "Inquiries",
      "Added By",
      "Amount",
    ];
    const data = reportData.map((row) => [
      row.slip_date,
      row.transfer_id,
      row.inquiry_ids || "N/A",
      row.inquiry_count || 0,
      row.added_by || "N/A",
      toNumber(row.amount),
    ]);
    downloadExcel(
      `DPMC Bulk Payment Report${filterSummary ? ` (${filterSummary})` : ""}`,
      headers,
      data,
      `DPMC_Remittance_Report_${startDate}_${endDate}`,
      formatCurrency(totalAmount),
      undefined,
      undefined,
      `${reportData.length} Bulk Transfers`,
    );
  };

  if (permissionLoading) {
    return <LoadingScreen messageQueue={"Loading..."} />;
  }

  if (!hasPermission) {
    return <NoAccessScreen />;
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center px-6 pb-5">
        <h1 className="text-2xl font-semibold">Bulk Payment to DPMC (Remittance)</h1>
        <div className="flex gap-3">
          <Button
            onClick={openDrawer}
            className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white border-none shadow-sm px-4 h-10 rounded-lg transition-all"
          >
            <SlidersHorizontal className="w-4 h-4 text-white" />
            <span className="font-medium text-sm">Filters</span>
          </Button>
          <Button
            onClick={handleDownloadExcel}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white border-none shadow-sm px-4 h-10 rounded-lg transition-all"
            disabled={reportData.length === 0}
          >
            <Download className="w-4 h-4 text-white" />
            <span className="font-medium text-sm">Excel</span>
          </Button>
        </div>
      </div>

      <div className="bg-white mx-6 mb-6 p-4 rounded-xl shadow-sm">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Send className="w-4 h-4 text-emerald-600" />
          <span>Filters: </span>
          <span className="font-medium text-gray-700">
            {filterSummary || "None applied — open Filters to pick a period"}
          </span>
        </div>
      </div>

      {reportData.length > 0 && (
        <div className="px-6 pb-6">
          {/* Green Summary Card */}
          <div className="bg-emerald-600 rounded-xl shadow-md overflow-hidden mb-6 p-6 text-white text-sm">
            <div className="flex flex-wrap gap-12">
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">TOTAL REMITTED TO DPMC</p>
                <p className="text-2xl font-bold">{formatCurrency(totalAmount)}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">BULK TRANSFERS</p>
                <p className="text-2xl font-bold">{reportData.length}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">INQUIRIES SETTLED</p>
                <p className="text-2xl font-bold">
                  {reportData.reduce((acc, r) => acc + toNumber(r.inquiry_count), 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col">
            <div className="flex-grow overflow-auto max-h-[calc(100vh-320px)]">
              {loading ? (
                <div className="flex justify-center items-center py-20">
                  <Spin size="large" />
                </div>
              ) : (
                <Table className="min-w-max text-sm">
                  <TableHeader className="bg-gray-100/50">
                    <TableRow className="border-b">
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Date</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Transfer ID</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Inquiry IDs</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Inquiries</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Added By</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.map((row, index) => (
                      <TableRow
                        key={`${row.transfer_id}-${index}`}
                        className="border-b last:border-0 hover:bg-gray-50"
                      >
                        <TableCell className="px-4 py-3">{row.slip_date}</TableCell>
                        <TableCell className="px-4 py-3 text-gray-500 font-mono text-xs">{row.transfer_id}</TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex flex-wrap gap-1 max-w-md">
                            {(row.inquiry_ids || "N/A").split(", ").map((inq: string, i: number) => (
                              <span
                                key={i}
                                className="px-2 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-100 font-mono text-[10px]"
                              >
                                {inq}
                              </span>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">{row.inquiry_count || 0}</TableCell>
                        <TableCell className="px-4 py-3">{row.added_by || "N/A"}</TableCell>
                        <TableCell className="px-4 py-3 text-right font-medium">
                          {formatCurrency(toNumber(row.amount))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && reportData.length === 0 && hasApplied && (
        <div className="px-6 pb-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col items-center justify-center py-20 text-gray-500">
            <p>No DPMC remittances recorded for the applied period.</p>
          </div>
        </div>
      )}
      {!loading && reportData.length === 0 && !hasApplied && (
        <div className="px-6 pb-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col items-center justify-center py-20 text-gray-500">
            <p>Open Filters and pick a period to view DPMC bulk remittances.</p>
          </div>
        </div>
      )}

      <Drawer
        title="DPMC Remittance Report Filters"
        placement="right"
        width={400}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        footer={
          <div className="flex justify-end gap-3">
            <AntdButton
              onClick={resetFilters}
              className="flex items-center gap-2 border-gray-200"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </AntdButton>
            <AntdButton
              type="primary"
              onClick={applyFilters}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Apply Filters
            </AntdButton>
          </div>
        }
      >
        <Space direction="vertical" size="large" className="w-full">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Date Range
            </label>
            <RangePicker
              value={draftRange}
              onChange={(dates) => setDraftRange(dates)}
              className="w-full"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Added User
            </label>
            <Select
              className="w-full"
              size="large"
              placeholder="All Users"
              value={draftAddedUserId}
              onChange={(val: any) => setDraftAddedUserId(val)}
              allowClear
              showSearch
              optionFilterProp="children"
              options={users.map((u) => ({ value: u.id, label: u.username }))}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Transfer ID
            </label>
            <Input
              size="large"
              placeholder="e.g. 105623418118812"
              value={draftSlipId}
              onChange={(e) => setDraftSlipId(e.target.value)}
              allowClear
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Inquiry Number
            </label>
            <Input
              size="large"
              placeholder="e.g. PNQNLY2026021203001"
              value={draftInquiryNumber}
              onChange={(e) => setDraftInquiryNumber(e.target.value)}
              allowClear
            />
          </div>
        </Space>
      </Drawer>
    </div>
  );
};